// Persistence is SERVER-FIRST through BE-Light (`Back-End-Light__report-branding` — filed with this
// feature; user work-product must not live only in one browser) with a localStorage fallback so the
// feature works before the endpoints land and degrades gracefully offline. The fallback mirrors the
// server shape exactly, so the swap is invisible to callers.
import { api } from "../api";

export interface Branding {
  name: string;
  /* * multiline; rendered as address lines under the name in the letter's signature block */
  address: string;
  /* * JPEG data URL (normalized at upload) or null */
  logo: string | null;
}

export interface BrandingState {
  org: Branding | null;
  personal: Branding | null;
  use: "org" | "personal";
}

const LS_PERSONAL = "sm_branding_personal";
const LS_USE = "sm_branding_use";
const lsOrgKey = (orgId: string) => `sm_branding_org:${orgId}`;

const readLs = (key: string): Branding | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const b = JSON.parse(raw) as Branding;
    return typeof b?.name === "string"
      ? { name: b.name, address: b.address ?? "", logo: b.logo ?? null }
      : null;
  } catch {
    return null;
  }
};

/* * The full branding picture for the active org — server first, localStorage fallback. */
export async function fetchBrandingState(
  orgId: string,
): Promise<BrandingState> {
  try {
    return await api.fetchBranding(orgId);
  } catch {
    const use = localStorage.getItem(LS_USE);
    return {
      org: readLs(lsOrgKey(orgId)),
      personal: readLs(LS_PERSONAL),
      use: use === "personal" ? "personal" : "org",
    };
  }
}

export async function savePersonalBranding(b: Branding): Promise<void> {
  localStorage.setItem(LS_PERSONAL, JSON.stringify(b)); // fallback copy always kept current
  try {
    await api.savePersonalBranding(b);
  } catch {
    /* server not there yet — the local copy carries it */
  }
}

export async function saveOrgBranding(
  orgId: string,
  b: Branding,
): Promise<void> {
  localStorage.setItem(lsOrgKey(orgId), JSON.stringify(b));
  try {
    await api.saveOrgBranding(orgId, b);
  } catch {
    /* fallback */
  }
}

export async function saveBrandingUse(use: "org" | "personal"): Promise<void> {
  localStorage.setItem(LS_USE, use);
  try {
    await api.saveBrandingUse(use);
  } catch {
    /* fallback */
  }
}

/* * The branding the user's CHOICE resolves to — null = none set up, Branded Report unavailable. */
export function effectiveBranding(state: BrandingState): Branding | null {
  const chosen = state.use === "personal" ? state.personal : state.org;
  return chosen && chosen.name.trim() !== "" ? chosen : null;
}

/* * data URL → bytes (for the PDF embedder) */
export function dataUrlBytes(dataUrl: string): Uint8Array | null {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return null;
  try {
    const bin = atob(dataUrl.slice(comma + 1));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/* * Normalize an uploaded image to a small baseline-JPEG data URL (≤600×300, white matte). */
export async function normalizeLogo(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = () =>
        rej(new Error("That file could not be read as an image."));
      el.src = url;
    });
    const s = Math.min(1, 600 / img.naturalWidth, 300 / img.naturalHeight);
    const w = Math.max(1, Math.round(img.naturalWidth * s));
    const h = Math.max(1, Math.round(img.naturalHeight * s));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx)
      throw new Error("Image processing is unavailable in this browser.");
    ctx.fillStyle = "#ffffff"; // transparent PNGs need a matte — JPEG has no alpha
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}
